import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import {
    Products,
    useModal,
    noticeError,
    getHeapUserId,
    trackHeapEvent,
    getReferrerURL,
    getAffiliateData,
    navigateWindowTo,
    GiftOfferIncentiveModal,
    AFFILIATE_QUERY_PARAM_KEYS,
    GIFT_OFFER_LEAD_QUERY_PARAMS,
    LightboxFullScreenTakeoverStyles,
} from '@ratehub/base-ui';

import trackHeapEventForLoans from '../functions/trackHeapEventForLoans';
import trackHeapLoanRedirectEvent from '../functions/trackHeapLoanRedirectEvent';


/**
 * @typedef {import('@ratehub/loans-finder').LoanShape} LoanShape - FE’s loan shape
 *
 * @callback onApplyNowCallback
 * @param {LoanShape} loan - the selected loan
 * @param {boolean} [isOfferAccepted=false] - has the user accepted the offer?
 *
 * @typedef {object} ApplyForLoanArgument
 * @property {LoanShape} loan - the loan object
 * @property {onApplyNowCallback} [onApplyNow] - callback invoked when apply now is clicked
 * @property {string} [applyURLOverride] - URL override for the apply link
 * @property {string} [provinceCode] - province code
 * @property {boolean} [enableRedirect=true] - whether to redirect after applying (defaults to true)
 * @property {string} [componentName] - component name for tracking
 */

/**
 * Hook that returns a memoized callback for applying for a loan.
 * When the loan includes a gift offer, it will open the gift offer modal before proceeding with normal processing.
 * @returns {function(ApplyForLoanArgument): (null|undefined)} A memoized callback function
 */
function useApplyForLoanCallback() {
    const modal = useModal();

    return useCallback(
        /**
         * Handles the loan application flow.
         * @param {ApplyForLoanArgument} arg - the apply for loan argument object
         * @returns {void} - don’t care
         */
        ({
            loan,
            onApplyNow,
            applyURLOverride,
            provinceCode,
            enableRedirect = true,
            componentName,
        }) => {
            if (!loan) {
                noticeError(new RangeError('`loan` argument to loan callback must not be empty.'));

                return;
            }

            trackHeapEventForLoans(loan);

            const {
                slug,
                detailsURL,
                provider,
                applyRedirectURL,
                isMonetized,
                isSponsored,
                hasReferrerOffer,
                giftOffer,
            } = loan;

            // alternatively, there may be a gift offer that a 3rd party deals with instead of us
            const isRatehubGiftOffer = !!giftOffer && !giftOffer.is3rdPartyFulfillment;
            // TODO this assumes in the future Loans will be referral eligible
            //      when it is, implement isLoanEligibleForReferralOffer()
            const isReferralEligible = hasReferrerOffer;   // isLoanEligibleForReferralOffer({ isRatehubGiftOffer, hasReferralOffer });

            const navigateQueryParams = {
                sponsored: isSponsored,
                heap_id: getHeapUserId(),
                ...(provinceCode && { province: provinceCode }),
                ...(componentName && { component_name: componentName }),
            };


            // do the standard onApplyNow() action when there is no gift card offer AND it is not referral eligible,
            // navigate if enableRedirect, and then we are done; no modal will open
            if (!isRatehubGiftOffer && !isReferralEligible) {
                trackHeapEvent('Loan conversion attempt', {
                    href: detailsURL,
                    slug: slug,
                    provider: provider.name,
                    monetized: isMonetized.toString(),
                    sponsored: isSponsored.toString(),
                });

                trackHeapLoanRedirectEvent({
                    referrerURL: getReferrerURL(),
                    affiliateId: getAffiliateData()?.[AFFILIATE_QUERY_PARAM_KEYS.affiliateId],
                    loan,
                });

                onApplyNow?.(loan);
                if (enableRedirect) {
                    navigateWindowTo(applyURLOverride ?? applyRedirectURL, {
                        isNewTab: true,
                        queryParams: navigateQueryParams,
                    });
                }

                return;
            }


            // If we did not exit early with the return directly above:
            //  • there IS a gift offer OR a referral offer, AND
            //  • we have been asked to show it (by context or by experiment)
            // If this is invoked, we know they explicitly accepted or declined the offer.
            // They did NOT cancel, and we are about to send them to the provider’s site.
            function handleAcceptOrDecline(additionalInfo) {
                modal.close(modalId);

                const { email, firstName, lastName, isOfferAccepted } = additionalInfo;

                trackHeapLoanRedirectEvent({
                    referrerURL: getReferrerURL(),
                    affiliateId: getAffiliateData()?.[AFFILIATE_QUERY_PARAM_KEYS.affiliateId],
                    additionalInfo,
                    loan,
                });

                onApplyNow?.(loan, isOfferAccepted);
                if (enableRedirect) {
                    navigateWindowTo(applyURLOverride ?? applyRedirectURL, {
                        isNewTab: true,
                        queryParams: {
                            ...navigateQueryParams,
                            ...(isOfferAccepted && {
                                [GIFT_OFFER_LEAD_QUERY_PARAMS.email]: email,
                                [GIFT_OFFER_LEAD_QUERY_PARAMS.firstName]: firstName,
                                // make sure to send an empty string to confirm intentional blankness
                                [GIFT_OFFER_LEAD_QUERY_PARAMS.lastName]: lastName ?? '',
                                [GIFT_OFFER_LEAD_QUERY_PARAMS.isGiftCardLead]: isOfferAccepted,
                            }),
                        },
                    });
                }
            }

            // We use the modal’s layer id to close the modal programmatically
            const modalId = modal.open({
                isContentFullWidth: true,
                showBranding: false,
                verticalAlignment: 'flex-start',

                style: LightboxFullScreenTakeoverStyles,

                // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
                screenReaderLabel: isRatehubGiftOffer
                    ? (
                        <FormattedMessage
                            id="loans.gift-offer.screen-reader"
                            defaultMessage="Loan gift offer"
                        />
                    )
                    : (
                        <FormattedMessage
                            id="loans.referral-offer.screen-reader"
                            defaultMessage="Referral offer bonus"
                        />
                    ),

                children: (
                    <GiftOfferIncentiveModal
                        giftOffer={loan.giftOffer}
                        providerName={loan.provider.name}
                        productType={Products.LOANS}
                        hasNewsletterOffer={false}
                        onAcceptOrDecline={handleAcceptOrDecline}
                        isReferralEligible={isReferralEligible}
                    />
                ),
            });
        },
        [ modal ],
    );
}


export default useApplyForLoanCallback;
